import React, { useState, useEffect } from "react";
import {
  Menu,
  Phone,
  Mail,
  MapPin,
  Hammer,
  Ruler,
  ClipboardCheck,
  Building2,
  ChevronRight,
  CheckCircle2,
  X,
  ChevronLeft,
} from "lucide-react";
import { motion } from "framer-motion";
import { XMLParser } from "fast-xml-parser";
import DOMPurify from "dompurify";

// Chargement automatique de tous les fichiers JSON dans src/realisations
const realisationsContext = require.context("./realisations", false, /\.json$/);
const realisations = realisationsContext.keys().map((key) =>
  realisationsContext(key)
);

const getMainImage = (real) => {
  if (real.gallery && real.gallery.length > 0) {
    const first = real.gallery[0];
    // selon la forme du JSON, ce sera soit { image: "/..." } soit "/..."
    return typeof first === "string" ? first : first.image;
  }
  // fallback si anciennes données avec "image" existent encore
  return real.image || "/uploads/placeholder.png";
};
const getGalleryImages = (real) => {
  if (!real) return [];
  if (real.gallery && real.gallery.length > 0) {
    // gallery = [{ image: "/truc.jpg" }, ...] ou ["/truc.jpg", ...]
    return real.gallery.map((g) => g.image || g);
  }
  return [real.image].filter(Boolean);
};

function parseSynermiXmlToArray(xmlText) {
  const parser = new XMLParser({ ignoreAttributes: false });
  const json = parser.parse(xmlText);

  // On s’attend à un truc du style { annonces: { annonce: [...] } }
  const raw = json.annonces?.annonce || [];

  // Si une seule annonce, raw peut être un objet → on force un tableau
  return Array.isArray(raw) ? raw : [raw];
}

function normalizeAnnonce(raw, category) {
  const prix = Number(raw.prix_min) || 0;
  const surface = Number(raw.superficie_terrain_min) || 0;

  const images = [];
  for (let i = 1; i <= 10; i++) {
    const key = `photo${i}`;
    if (raw[key]) {
      images.push(raw[key]);
    }
  }

  return {
    id: raw["@_id"] || raw.ordre || null,
    type: category === "t" ? "Terrain" : "Terrain + Maison",
    titre: raw.titre || "",
    ville: raw.ville || raw.villebdd?.nom || "",
    departement: raw.villebdd?.dept
      ? String(raw.villebdd.dept).replace(/^0/, "")
      : "",
    cp: raw.cp?.toString() || "",
    prix,
    budget: prix,
    surface,
    image: images[0] || raw.photo1 || "",

    images,

    // on garde ça pour plus tard (fiche annonce éventuelle)
    details: {
      contenu: raw.contenu_terrain || raw.contenu_affiliation || "",
      latitude: raw.lat,
      longitude: raw.lon,
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Composant Typeform : RechercheBiens                  */
/* -------------------------------------------------------------------------- */

function RechercheBiens({ onBack }) {
  useEffect(() => {
    window.scrollTo({ top: 100, behavior: "smooth" });
  }, []);
  const [step, setStep] = useState(0);
  const totalSteps = 6;
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [selectedAnnonceIndex, setSelectedAnnonceIndex] = useState(0);

  const [answers, setAnswers] = useState({
    typeBien: "",
    departement: "",
    ville: "",
    budgetMax: 250000,
    surfaceMin: 100,
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    commentaire: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [villes, setVilles] = useState([]);
  const [villesLoading, setVillesLoading] = useState(false);
  const [villesError, setVillesError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!results || results.length === 0) return;

    results.forEach((b) => {
      const imgs =
        b.images && b.images.length > 0
          ? b.images
          : [b.image].filter(Boolean);

      imgs.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, [results]);

  // Chargement des villes via GeoAPI quand le département change
  useEffect(() => {
    if (!answers.departement) {
      setVilles([]);
      return;
    }

    setVillesLoading(true);
    setVillesError(null);

    fetch(
      `https://geo.api.gouv.fr/departements/${answers.departement}/communes?fields=nom&limit=500&boost=population`
    )
      .then((res) => res.json())
      .then((data) => {
        const noms = data.map((c) => c.nom).sort();
        setVilles(noms);
        if (!noms.includes(answers.ville)) {
          setAnswers((prev) => ({ ...prev, ville: "" }));
        }
      })
      .catch((err) => {
        console.error(err);
        setVillesError(
          "Impossible de charger les communes pour ce département."
        );
      })
      .finally(() => {
        setVillesLoading(false);
      });
  }, [answers.departement, answers.ville]);

  // Réglages dynamiques pour la surface selon le type de bien
  const getSurfaceSettings = () => {
    const type = answers.typeBien;
    if (type === "Maison neuve" || type === "Maison ancienne à rénover") {
      return {
        min: 40,
        max: 300,
        step: 5,
        label: "Surface minimale (m² habitables)",
      };
    }
    if (type === "Terrain") {
      return {
        min: 200,
        max: 3000,
        step: 50,
        label: "Surface minimale du terrain (m²)",
      };
    }
    return {
      min: 20,
      max: 1000,
      step: 10,
      label: "Surface minimale (m²)",
    };
  };

  const surfaceSettings = getSurfaceSettings();

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleChange = (field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  // 🔹 Fonction générique d’envoi à Netlify
  const sendRechercheForm = (extraFields = {}) => {
    const formData = new FormData();
    formData.append("form-name", "recherche-biens");

    // Réponses utilisateur
    Object.entries(answers).forEach(([key, value]) => {
      formData.append(key, value ?? "");
    });

    // Champs supplémentaires (résultats, annonce choisie…)
    Object.entries(extraFields).forEach(([key, value]) => {
      formData.append(key, value ?? "");
    });

    return fetch("/", {
      method: "POST",
      body: formData,
    });
  };

  // 🔹 Bouton "Ça m'intéresse"
  const handleInteresse = (bien) => {
    const resume = `${bien.type} - ${bien.titre} - ${bien.ville} (${bien.departement}) - ${bien.prix.toLocaleString(
      "fr-FR"
    )} €`;

    sendRechercheForm({
      resultats_count: "1",
      resultats_biens: resume,
      annonce_id: bien.id ?? "",
      annonce_titre: bien.titre ?? "",
      annonce_ville: bien.ville ?? "",
      annonce_departement: bien.departement ?? "",
      annonce_prix: bien.prix?.toString() ?? "",
    })
      .catch(() => {
        // optionnel : console.error
      })
      .finally(() => {
        window.location.href = "/merci/";
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setIsLoading(true);

    let annoncesTerrain = [];
    let annoncesMaison = [];

    try {
      // TERRAIN SEUL
      const resT = await fetch(
        "/.netlify/functions/annonces?type=24&categorie=t"
      );
      const xmlT = await resT.text();
      annoncesTerrain = parseSynermiXmlToArray(xmlT).map((a) =>
        normalizeAnnonce(a, "t")
      );

      // TERRAIN + MAISON
      const resM = await fetch(
        "/.netlify/functions/annonces?type=24&categorie=m"
      );
      const xmlM = await resM.text();
      annoncesMaison = parseSynermiXmlToArray(xmlM).map((a) =>
        normalizeAnnonce(a, "m")
      );
    } catch (err) {
      console.error(err);
    }

    const allBiens = [...annoncesTerrain, ...annoncesMaison];

    const filtered = allBiens.filter((b) => {
      if (answers.typeBien === "Terrain" && b.type !== "Terrain") return false;

      if (
        (answers.typeBien === "Maison neuve" ||
          answers.typeBien === "Maison ancienne à rénover") &&
        b.type !== "Terrain + Maison"
      ) {
        return false;
      }

      if (answers.departement && b.departement !== answers.departement) {
        return false;
      }

      if (answers.ville && b.ville !== answers.ville) {
        return false;
      }

      if (answers.budgetMax && b.prix > answers.budgetMax) return false;

      if (answers.surfaceMin && b.surface < answers.surfaceMin) return false;

      return true;
    });

    // Cas spéciaux : "Maison ancienne à rénover" ou "Autre" → pas de matching auto
    let finalResults;
    if (
      answers.typeBien === "Maison ancienne à rénover" ||
      answers.typeBien === "Autre"
    ) {
      finalResults = [];
      setResults([]);
    } else {
      finalResults = filtered;
      setResults(filtered);
    }

    // 👉 CAS 1 : aucun bien correspondant → on envoie immédiatement
    if (finalResults.length === 0) {
      const resumeBiens =
        "Aucun bien trouvé (ou type de bien impossible à matcher).";

      sendRechercheForm({
        resultats_count: "0",
        resultats_biens: resumeBiens,
      }).catch(() => {
        // optionnel : console.error
      });
    }

    // 👉 CAS 2 : il y a des résultats → on attend le clic sur "Ça m'intéresse"
    setIsLoading(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-4">
              Quel type de bien recherchez-vous ?
            </h2>
            <p className="text-gray-600 mb-6">
              Choisissez la catégorie qui correspond le mieux à votre projet.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Maison neuve",
                "Maison ancienne à rénover",
                "Terrain",
                "Autre",
              ].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    handleChange("typeBien", type);
                    handleNext();
                  }}
                  className={`border rounded-2xl px-4 py-3 text-left transition hover:shadow-sm ${
                    answers.typeBien === type
                      ? "border-[#020073] bg-[#020073]/5"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="font-medium">{type}</div>
                </button>
              ))}
            </div>
          </div>
        );
      case 1:
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-4">
              Dans quel département ?
            </h2>
            <p className="text-gray-600 mb-6">
              Sélectionnez le département principal de votre recherche.
            </p>
            <div className="flex flex-wrap gap-3">
              {["85", "49", "44"].map((dep) => (
                <button
                  key={dep}
                  type="button"
                  onClick={() => handleChange("departement", dep)}
                  className={`px-4 py-2 rounded-full border text-sm transition ${
                    answers.departement === dep
                      ? "border-[#020073] bg-[#020073] text-white"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  {dep}
                </button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-4">
              Dans quelle commune ?
            </h2>
            <p className="text-gray-600 mb-4">
              Choisissez une commune dans le département sélectionné ou laissez
              vide pour un périmètre plus large.
            </p>

            {villesLoading && (
              <div className="text-sm text-gray-500 mb-2">
                Chargement des communes…
              </div>
            )}
            {villesError && (
              <div className="text-sm text-red-500 mb-2">{villesError}</div>
            )}

            {answers.departement ? (
              <select
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073] bg-white"
                value={answers.ville}
                onChange={(e) => handleChange("ville", e.target.value)}
              >
                <option value="">Sans préférence</option>
                {villes.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-gray-500">
                Commencez par choisir un département à l’étape précédente.
              </div>
            )}
          </div>
        );
      case 3:
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-4">
              Quel est votre budget maximum ?
            </h2>
            <p className="text-gray-600 mb-4">
              Indiquez une enveloppe globale approximative.
            </p>
            <div className="mb-2 text-sm font-medium text-[#020073]">
              {answers.budgetMax.toLocaleString("fr-FR")} € maximum
            </div>
            <input
              type="range"
              min={20000}
              max={600000}
              step={5000}
              value={answers.budgetMax}
              onChange={(e) =>
                handleChange("budgetMax", Number(e.target.value))
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>20 000 €</span>
              <span>600 000 €</span>
            </div>
          </div>
        );
      case 4:
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-4">
              Surface minimale
            </h2>
            <p className="text-gray-600 mb-4">{surfaceSettings.label}</p>
            <div className="mb-2 text-sm font-medium text-[#020073]">
              {answers.surfaceMin} m² minimum
            </div>
            <input
              type="range"
              min={surfaceSettings.min}
              max={surfaceSettings.max}
              step={surfaceSettings.step}
              value={answers.surfaceMin}
              onChange={(e) =>
                handleChange("surfaceMin", Number(e.target.value))
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{surfaceSettings.min} m²</span>
              <span>{surfaceSettings.max} m²</span>
            </div>
          </div>
        );
      case 5:
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-4">
              Vos coordonnées
            </h2>
            <p className="text-gray-600 mb-4">
              Ces informations permettront à Ragot Conception de vous
              recontacter rapidement avec une sélection de biens adaptée.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium">Nom*</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                  value={answers.nom}
                  onChange={(e) => handleChange("nom", e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium">Prénom*</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                  value={answers.prenom}
                  onChange={(e) => handleChange("prenom", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium">Email*</label>
                <input
                  type="email"
                  className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                  value={answers.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium">Téléphone*</label>
                <input
                  type="tel"
                  inputMode="tel"
                  pattern="[0-9+ ]*"
                  className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                  value={answers.telephone}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9+ ]/g, "");
                    handleChange("telephone", cleaned);
                  }}
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium">
                Précisions sur votre projet (facultatif)
              </label>
              <textarea
                rows={4}
                className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                placeholder="Ex : délai souhaité, contraintes particulières, financement, etc."
                value={answers.commentaire}
                onChange={(e) => handleChange("commentaire", e.target.value)}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const canGoNext = () => {
    if (step === 0) return !!answers.typeBien;
    if (step === 1) return !!answers.departement;
    if (step === 3) return !!answers.budgetMax;
    if (step === 4) return !!answers.surfaceMin;
    if (step === 5)
      return (
        !!answers.nom &&
        !!answers.prenom &&
        !!answers.email &&
        !!answers.telephone
      );
    return true;
  };

  return (
    <main className="py-16 md:py-24 bg-gray-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-4">
        <button
          type="button"
          onClick={onBack}
          className="mb-6 text-sm text-[#020073] hover:underline"
        >
          ← Retour au site
        </button>

        {/* Formulaire Netlify caché pour la recherche de biens */}
        <form
          name="recherche-biens"
          data-netlify="true"
          netlify-honeypot="bot-field"
          hidden
        >
          <input type="hidden" name="form-name" value="recherche-biens" />
          <input name="bot-field" />
        </form>

        {/* Formulaire multi-étapes */}
        {!isSubmitted && (
          <>
            <h1 className="text-2xl md:text-3xl font-bold text-[#020073] mb-2">
              Recherche de biens
            </h1>
            <p className="text-gray-600 mb-6">
              Répondez à quelques questions pour que Ragot Conception puisse
              vous proposer une sélection ciblée de terrains ou de maisons.
            </p>

            {/* Progression */}
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Étape {step + 1} / {totalSteps}
              </div>
              <div className="w-40 h-1 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-[#020073] transition-all duration-300"
                  style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl border p-6 md:p-8 shadow-sm"
            >
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25 }}
              >
                {renderStep()}
              </motion.div>

              {/* Boutons de navigation */}
              <div className="mt-8 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={step === 0}
                  className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl border ${
                    step === 0
                      ? "border-gray-200 text-gray-300 cursor-not-allowed"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour
                </button>

                {step < totalSteps - 1 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canGoNext()}
                    className={`inline-flex items-center gap-2 text-sm px-5 py-2 rounded-2xl ${
                      canGoNext()
                        ? "bg-[#020073] text-white hover:bg-[#020073]/90"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Continuer
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canGoNext() || isLoading}
                    className={`inline-flex items-center gap-2 text-sm px-5 py-2 rounded-2xl ${
                      canGoNext() && !isLoading
                        ? "bg-[#020073] text-white hover:bg-[#020073]/90"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {isLoading
                      ? "Recherche en cours..."
                      : "Voir les biens correspondants"}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
          </>
        )}

        {/* Résultats après soumission */}
        {isSubmitted && (
          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-semibold text-[#020073] mb-3">
              Résultats de votre recherche
            </h2>

            {isLoading ? (
              <div className="flex items-center gap-3 text-sm text-gray-600 border rounded-xl bg-white px-4 py-3">
                <div className="h-5 w-5 border-2 border-[#020073] border-t-transparent rounded-full animate-spin" />
                <span>Recherche des biens correspondant à vos critères…</span>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-4 text-sm">
                  {answers.typeBien === "Terrain" &&
                    "Voici les terrains correspondant à vos critères, issus de notre base de biens partenaires."}

                  {(answers.typeBien === "Maison neuve" ||
                    answers.typeBien === "Maison ancienne à rénover") &&
                    "Voici les offres Terrain + Maison correspondant à vos critères."}

                  {answers.typeBien === "Autre" &&
                    "Voici les biens correspondant à vos critères."}
                </p>

                {results.length === 0 ? (
                  <div className="text-sm text-gray-500 border rounded-xl bg-white px-4 py-3">
                    Aucun bien ne correspond exactement à vos critères parmi nos
                    annonces en ligne pour le moment. Votre demande a bien été
                    prise en compte et l’équipe Ragot Conception reviendra
                    rapidement vers vous pour rechercher des opportunités
                    adaptées à votre projet.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {results.map((b) => (
                      <div
                        key={b.id}
                        className="rounded-2xl border bg-white overflow-hidden shadow-sm hover:shadow-md transition flex flex-col"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAnnonce(b);
                            setSelectedAnnonceIndex(0);
                          }}
                          className="text-left w-full"
                        >
                          <div className="aspect-[4/3] overflow-hidden">
                            <img
                              src={b.image}
                              alt={b.titre}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </button>

                        <div className="p-4 flex flex-col gap-2 flex-1">
                          <div className="text-xs text-gray-500">
                            {b.type} • {b.ville} ({b.departement})
                          </div>

                          <h3 className="font-semibold text-sm">{b.titre}</h3>

                          <div className="flex items-baseline justify-between">
                            <div className="text-sm text-[#020073] font-semibold">
                              {b.prix.toLocaleString("fr-FR")} €
                            </div>
                            {b.surface > 0 && (
                              <div className="text-xs text-gray-600">
                                {b.surface} m²
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Modale d'annonce */}
        {selectedAnnonce && (() => {
          const images =
            selectedAnnonce.images && selectedAnnonce.images.length > 0
              ? selectedAnnonce.images
              : [selectedAnnonce.image].filter(Boolean);

          const hasMultiple = images.length > 1;

          const goPrev = () => {
            setSelectedAnnonceIndex((prev) =>
              prev === 0 ? images.length - 1 : prev - 1
            );
          };

          const goNext = () => {
            setSelectedAnnonceIndex((prev) =>
              prev === images.length - 1 ? 0 : prev + 1
            );
          };

          return (
            <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-2 sm:px-4 py-4">
              {/* Fond sombre cliquable */}
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => setSelectedAnnonce(null)}
              />

              {/* Contenu de la modale */}
              <div className="relative bg-white rounded-2xl w-full sm:max-w-3xl max-h-[90vh] overflow-hidden shadow-lg flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">
                      {selectedAnnonce.type} • {selectedAnnonce.ville} (
                      {selectedAnnonce.departement})
                    </div>
                    <h3 className="text-lg font-semibold">
                      {selectedAnnonce.titre}
                    </h3>
                    <div className="mt-1 text-sm text-[#020073] font-semibold">
                      {selectedAnnonce.prix.toLocaleString("fr-FR")} €
                      {selectedAnnonce.surface > 0 && (
                        <span className="text-gray-500 text-xs ml-2">
                          • {selectedAnnonce.surface} m²
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedAnnonce(null)}
                    className="p-2 rounded-full hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Image */}
                <div className="relative px-4 pt-4 pb-2">
                  <div className="h-64 sm:h-[55vh] flex items-center justify-center">
                    <img
                      src={images[selectedAnnonceIndex]}
                      className="max-h-full max-w-full rounded-xl object-contain shadow-lg"
                      alt=""
                    />
                  </div>

                  {hasMultiple && (
                    <>
                      <button
                        type="button"
                        onClick={goPrev}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 shadow"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      <button
                        type="button"
                        onClick={goNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 shadow"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>

                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                        {images.map((_, i) => (
                          <span
                            key={i}
                            className={`h-2 w-2 rounded-full ${
                              i === selectedAnnonceIndex
                                ? "bg-[#020073]"
                                : "bg-white/70"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Description scrollable */}
                {selectedAnnonce.details?.contenu && (
                  <div className="px-4 sm:px-6 py-4 text-sm text-gray-700 max-h-48 overflow-auto border-t">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          selectedAnnonce.details.contenu,
                          {
                            ALLOWED_TAGS: [
                              "p",
                              "br",
                              "strong",
                              "em",
                              "ul",
                              "ol",
                              "li",
                            ],
                            ALLOWED_ATTR: [],
                          }
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Footer actions */}
                <div className="px-4 sm:px-6 py-4 border-t flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setSelectedAnnonce(null)}
                    className="px-4 py-2 rounded-xl border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Fermer
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInteresse(selectedAnnonce)}
                    className="px-5 py-2 rounded-2xl bg-[#020073] text-white text-sm hover:bg-[#020073]/90"
                  >
                    Ça m’intéresse
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/* Composant principal                             */
/* -------------------------------------------------------------------------- */

export default function RagotConceptionSite() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedRealisation, setSelectedRealisation] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [view, setView] = useState("home");
  // 1. Nouvel état pour gérer le scroll différé
  const [pendingScroll, setPendingScroll] = useState(null);

  useEffect(() => {
    realisations.forEach((real) => {
      const imgs = getGalleryImages(real);
      imgs.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    });
  }, []);

  // 2. useEffect qui s'active quand on revient sur la home et qu'un scroll est en attente
  useEffect(() => {
    if (view === "home" && pendingScroll) {
      const timer = setTimeout(() => {
        scrollTo(pendingScroll);
        setPendingScroll(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [view, pendingScroll]);

  const openRealisation = (real) => {
    setSelectedRealisation(real);
    setCurrentImageIndex(0);
  };

  const scrollTo = (id) => {
    const isMobile = window.innerWidth < 768;

    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setMobileOpen(false);
      return;
    }

    let targetId = id;
    if (id === "contact-form" && !isMobile) {
      targetId = "contact-intro";
    }

    const el = document.getElementById(targetId);
    if (!el) return;

    if (isMobile) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

    setMobileOpen(false);
  };

  // 3. Fonction utilitaire pour gérer la navigation
  const handleNavClick = (targetId) => {
    if (view === "home") {
      scrollTo(targetId);
    } else {
      setView("home");
      setPendingScroll(targetId);
    }
    setMobileOpen(false);
  };

  const features = [
    {
      icon: <Ruler className="w-6 h-6" />,
      title: "Études préalables",
      desc: "Analyse du terrain, faisabilité, esquisses et estimation budgétaire.",
    },
    {
      icon: <Hammer className="w-6 h-6" />,
      title: "Conception",
      desc: "Plans, chiffrages, consultation des entreprises, suivi de chantier.",
    },
    {
      icon: <ClipboardCheck className="w-6 h-6" />,
      title: "Coordination",
      desc: "Pilotage des corps d'état, contrôle qualité, respect des délais et du budget.",
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Assistance admin.",
      desc: "Démarches permis / déclarations, conseils techniques et réglementaires.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-16 flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => handleNavClick("top")}
              aria-label="Retour en haut de page"
            >
              <img
                src="/logos/logo_quadri.png"
                alt="Ragot Conception"
                className="h-10 w-auto hidden sm:block transition-transform hover:scale-105"
              />
              <div className="sm:hidden w-10 h-10 rounded-xl grid place-items-center">
                <img
                  src="/logos/Logo Ragot 192 x 192 px.jpg"
                  alt="Ragot Conception"
                  className="w-7 h-7"
                />
              </div>
              <div className="leading-tight">
                <div className="font-semibold text-[#020073] relative">
                  RAGOT CONCEPTION
                </div>
                <div className="text-xs text-gray-500">
                  Maîtrise d&apos;œuvre • Neuf &amp; Rénovation
                </div>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6 text-sm">
              <button
                onClick={() => handleNavClick("about")}
                className="relative hover:text-[#020073] group"
              >
                <strong>Société</strong>
                <span className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-0 bg-[#020073] transition-all duration-300 group-hover:w-full" />
              </button>
              <button
                onClick={() => handleNavClick("services")}
                className="relative hover:text-[#020073] group"
              >
                <strong>Services</strong>
                <span className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-0 bg-[#020073] transition-all duration-300 group-hover:w-full" />
              </button>
              <button
                onClick={() => {
                  setView("recherche");
                  setMobileOpen(false);
                }}
                className="relative hover:text-[#020073] group"
              >
                <strong>Recherche de biens</strong>
                <span className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-0 bg-[#020073] transition-all duration-300 group-hover:w-full" />
              </button>
              <button
                onClick={() => handleNavClick("realisations")}
                className="relative hover:text-[#020073] group"
              >
                <strong>Réalisations</strong>
                <span className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-0 bg-[#020073] transition-all duration-300 group-hover:w-full" />
              </button>
              <button
                onClick={() => handleNavClick("contact-form")}
                className="relative hover:text-[#020073] group"
              >
                <strong>Contact</strong>
                <span className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-0 bg-[#020073] transition-all duration-300 group-hover:w-full" />
              </button>
              <a
                href="tel:+33757789181"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#bf9959] text-[#020073] hover:bg-[#bf9959]/10"
              >
                <Phone className="w-4 h-4" /> <strong>07 57 78 91 81</strong>
              </a>
            </nav>
            <button
              className="md:hidden p-2 rounded-xl border"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t">
            <div className="max-w-6xl mx-auto px-4 py-3 grid gap-2 text-sm">
              <button
                onClick={() => handleNavClick("about")}
                className="text-left px-2 py-2 rounded-lg hover:bg-gray-50"
              >
                Société
              </button>
              <button
                onClick={() => handleNavClick("services")}
                className="text-left px-2 py-2 rounded-lg hover:bg-gray-50"
              >
                Services
              </button>
              <button
                onClick={() => {
                  setView("recherche");
                  setMobileOpen(false);
                }}
                className="text-left px-2 py-2 rounded-lg hover:bg-gray-50"
              >
                Recherche de biens
              </button>
              <button
                onClick={() => handleNavClick("realisations")}
                className="text-left px-2 py-2 rounded-lg hover:bg-gray-50"
              >
                Réalisations
              </button>
              <button
                onClick={() => handleNavClick("contact-form")}
                className="text-left px-2 py-2 rounded-lg hover:bg-gray-50"
              >
                Contact
              </button>
              <a
                href="tel:+33757789181"
                className="px-2 py-2 rounded-lg border border-[#bf9959] inline-flex items-center gap-2 text-[#020073]"
              >
                <Phone className="w-4 h-4" /> 07 57 78 91 81
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Vue HOME */}
      {view === "home" && (
        <>
          {/* Hero */}
          <section id="top" className="relative overflow-hidden">
            <div className="absolute inset-0">
              {/* Dégradé existant */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,#eef2ff,transparent_30%),radial-gradient(circle_at_80%_0%,#e2e8f0,transparent_35%)]" />

              {/* Image en fond, couvrant tout le bandeau */}
              <img
                src="/logos/FONDHOMERAGOT.png"
                alt="Fond Home"
                className="absolute inset-0 w-full h-full object-cover opacity-20 translate-x-20 md:translate-x-40"
              />
            </div>
            <div className="max-w-6xl mx-auto px-4 py-20 md:py-28 relative">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-4xl md:text-6xl font-extrabold tracking-tight"
              >
                Maîtrise d&apos;œuvre <span className="text-[#020073]"></span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-5 text-lg md:text-xl text-gray-600 max-w-3xl"
              >
                De l&apos;esquisse au suivi de chantier, Ragot Conception
                accompagne vos projets de construction, rénovation et extension.
              </motion.p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("contact-form");
                  }}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#020073] text-white hover:bg-[#020073]/90"
                >
                  Demander un rendez-vous <ChevronRight className="w-4 h-4" />
                </a>
                <a
                  href="#services"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("services");
                  }}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-[#bf9959] text-[#020073] hover:bg-[#bf9959]/10"
                >
                  Découvrir nos services
                </a>
              </div>
            </div>
          </section>

          {/* About */}
          <section id="about" className="py-16 md:py-24 border-t">
            <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-2xl md:text-3xl font-bold text-[#020073]">
                  Ragot Conception, votre maître d&apos;œuvre de proximité
                </h2>
                <p className="mt-4 text-gray-600">
                  Basée en{" "}
                  <strong>Vendée</strong>
                  , l&apos;agence intervient pour les particuliers et les
                  professionnels : maisons neuves, extensions, rénovations et
                  réhabilitations. Une approche sur-mesure, un interlocuteur
                  unique, et la garantie d&apos;un suivi rigoureux à chaque
                  étape.
                </p>
                <ul className="mt-6 grid gap-2 text-gray-700">
                  {[
                    "Conseil technique indépendant",
                    "Réseau d'entreprises locales qualifiées",
                    "Optimisation budget / planning / qualité",
                    "Accompagnement administratif complet",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#bf9959]" />{" "}
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden shadow-sm border border-[#bf9959]/30">
                  <img
                    src="/logos/Photohome.png"
                    alt="Suivi de chantier"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Services */}
          <section id="services" className="py-16 md:py-24 border-t bg-gray-50">
            <div className="max-w-6xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-[#020073]">
                Services
              </h2>
              <p className="mt-3 text-gray-600 max-w-3xl">
                Nous sécurisons votre projet de A à Z :
              </p>
              <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
                {features.map((f) => (
                  <div
                    key={f.title}
                    className="p-6 rounded-2xl bg-white border hover:shadow-sm transition"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#020073] text-white grid place-items-center">
                      {f.icon}
                    </div>
                    <h3 className="mt-4 font-semibold">{f.title}</h3>
                    <p className="mt-2 text-sm text-gray-600">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Bloc Recherche de biens (intro) */}
          <section
            id="biens"
            className="relative py-16 md:py-24 border-t bg-gray-50 overflow-hidden"
          >
            {/* Fleur dorée en arrière-plan */}
            <img
              src="/logos/fleur_doree.png"
              alt="Décor fleur dorée"
              className="pointer-events-none select-none absolute -right-20 -top-16 w-[320px] opacity-20 hidden md:block"
            />

            {/* Contenu principal */}
            <div className="relative max-w-6xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-[#020073]">
                Recherche de biens
              </h2>

              <p className="mt-3 text-gray-600 max-w-4xl">
                Nous proposons un accompagnement sur-mesure pour votre{" "}
                <strong>recherche foncière ou immobilière</strong> (terrains,
                maisons à rénover, locaux commerciaux...). Grâce à notre réseau
                de partenaires professionnels • <strong>notaires</strong>,
                <strong> agences immobilières</strong>, <strong>géomètres</strong>,{" "}
                <strong>aménageurs</strong> et{" "}
                <strong>services urbanisme</strong> • nous identifions des biens
                pertinents, vérifions la <em>faisabilité</em> (PLU, accès,
                servitudes) et sécurisons votre décision.
              </p>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setView("recherche")}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#020073] text-white hover:bg-[#020073]/90"
                >
                  Démarrer votre recherche <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>

          {/* Réalisations */}
          <section id="realisations" className="py-16 md:py-24 border-t">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#020073]">
                    Réalisations
                  </h2>
                </div>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick("contact-form");
                  }}
                  className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#bf9959] text-[#020073] hover:bg-[#bf9959]/10"
                >
                  Nous confier un projet <ChevronRight className="w-4 h-4" />
                </a>
              </div>

              {/* Cartes projets */}
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {realisations.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    Aucune réalisation publiée pour le moment.
                  </div>
                ) : (
                  realisations.map((p) => (
                    <button
                      key={p.title}
                      type="button"
                      onClick={() => openRealisation(p)}
                      className="group rounded-2xl overflow-hidden border bg-white text-left w-full"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={getMainImage(p)}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition"
                        />
                      </div>
                      <div className="p-4 flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-500">{p.tag}</div>
                          <div className="font-medium">{p.title}</div>
                          {p.commune && (
                            <div className="text-xs text-gray-400 mt-1">
                              {p.commune}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#bf9959]" />
                      </div>
                    </button>
                  ))
                )}
              </div>

              {/* Modal Réalisation */}
              {selectedRealisation && (() => {
                const galleryImages = getGalleryImages(selectedRealisation);
                const hasMultiple = galleryImages.length > 1;

                const goPrev = () => {
                  setCurrentImageIndex((prev) =>
                    prev === 0 ? galleryImages.length - 1 : prev - 1
                  );
                };

                const goNext = () => {
                  setCurrentImageIndex((prev) =>
                    prev === galleryImages.length - 1 ? 0 : prev + 1
                  );
                };

                return (
                  <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    {/* Fond sombre cliquable */}
                    <div
                      className="absolute inset-0 bg-black/40"
                      onClick={() => setSelectedRealisation(null)}
                    />

                    {/* Boîte de modale */}
                    <div className="relative bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-lg flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between px-6 py-4 border-b">
                        <div>
                          <div className="text-sm text-gray-500">
                            {selectedRealisation.tag}
                            {selectedRealisation.commune &&
                              ` • ${selectedRealisation.commune}`}
                            {selectedRealisation.annee &&
                              ` • ${selectedRealisation.annee}`}
                          </div>
                          <h3 className="text-lg font-semibold">
                            {selectedRealisation.title}
                          </h3>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedRealisation(null)}
                          className="p-2 rounded-full hover:bg-gray-100"
                          aria-label="Fermer"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Carrousel */}
                      {galleryImages.length > 0 && (
                        <div className="relative px-4 pt-4 pb-2">
                          <div className="h-[60vh] flex items-center justify-center">
                            <img
                              src={galleryImages[currentImageIndex]}
                              alt={selectedRealisation.title}
                              className="max-h-full max-w-full mx-auto rounded-xl object-cover shadow-lg"
                            />
                          </div>

                          {hasMultiple && (
                            <>
                              <button
                                type="button"
                                onClick={goPrev}
                                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow"
                                aria-label="Image précédente"
                              >
                                <ChevronLeft className="w-5 h-5" />
                              </button>
                              <button
                                type="button"
                                onClick={goNext}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow"
                                aria-label="Image suivante"
                              >
                                <ChevronRight className="w-5 h-5" />
                              </button>

                              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                                {galleryImages.map((_, i) => (
                                  <span
                                    key={i}
                                    className={`h-2 w-2 rounded-full ${
                                      i === currentImageIndex
                                        ? "bg-[#020073]"
                                        : "bg-white/70"
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      {selectedRealisation.description && (
                        <div className="px-6 py-4 text-sm text-gray-700">
                          {selectedRealisation.description}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-16 md:py-24 border-t">
            <div className="max-w-6xl mx-auto px-4">
              <h2 className="text-2xl md:text-3xl font-bold text-[#020073]">
                Contact
              </h2>
              <p id="contact-intro" className="mt-3 text-gray-600">
                Dites-nous quelques mots sur votre projet.
              </p>

              <div className="mt-8 grid lg:grid-cols-3 gap-8">
                {/* Infos */}
                <div className="p-6 rounded-2xl border bg-gray-50">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-[#bf9959]" />
                    <span>
                      81 rue Georges Clemenceau, 85210 Saint Jean d'Hermine
                    </span>
                  </div>
                  <a
                    className="mt-3 flex items-center gap-3"
                    href="tel:+33757789181"
                  >
                    <Phone className="w-5 h-5 text-[#bf9959]" />
                    07 57 78 91 81
                  </a>
                  <a
                    className="mt-2 flex items-center gap-3"
                    href="tel:+33285759694"
                  >
                    <Phone className="w-5 h-5 text-[#bf9959]" />
                    02 85 75 96 94
                  </a>
                  <a
                    className="mt-2 flex items-center gap-3"
                    href="mailto:contact@ragotconception.fr"
                  >
                    <Mail className="w-5 h-5 text-[#bf9959]" />
                    contact@ragotconception.fr
                  </a>
                  <div className="mt-6 text-sm text-gray-600">
                    Du lundi au vendredi 9h00-12h30 et de 14h00 à 17h00 ou sur
                    RDV en dehors de ces horaires. (Les rendez-vous se font à
                    l’agence ou à domicile selon la nature du projet et la
                    distance.)
                  </div>
                  <div className="mt-6 rounded-2xl overflow-hidden border shadow-sm">
                    <img
                      src="/logos/PHOTOAGENCE.jpg"
                      alt="Agence Ragot Conception"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>

                {/* Formulaire */}
                <form
                  id="contact-form"
                  name="contact"
                  method="POST"
                  action="/merci/"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  className="lg:col-span-2 p-6 rounded-2xl border"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <input type="hidden" name="bot-field" />

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nom" className="text-sm font-medium">
                        Nom*
                      </label>
                      <input
                        id="nom"
                        name="nom"
                        required
                        className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                        placeholder="Dupont"
                      />
                    </div>

                    <div>
                      <label htmlFor="prenom" className="text-sm font-medium">
                        Prénom*
                      </label>
                      <input
                        id="prenom"
                        name="prenom"
                        required
                        className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                        placeholder="Marie"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="text-sm font-medium">
                        Email*
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                        placeholder="vous@exemple.fr"
                      />
                    </div>

                    <div>
                      <label htmlFor="tel" className="text-sm font-medium">
                        Téléphone*
                      </label>
                      <input
                        id="tel"
                        name="telephone"
                        type="tel"
                        inputMode="tel"
                        pattern="[0-9+ ]*"
                        onInput={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9+ ]/g,
                            ""
                          );
                        }}
                        required
                        className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                        placeholder="06 00 00 00 00"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label htmlFor="projet" className="text-sm font-medium">
                        Votre projet*
                      </label>
                      <textarea
                        id="projet"
                        name="projet"
                        required
                        rows={5}
                        className="mt-1 w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#020073]"
                        placeholder="Maison neuve / extension / rénovation, commune, budget, délais…"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-600">
                      En envoyant ce formulaire, vous acceptez d’être
                      recontacté(e). Aucune donnée transmise à des tiers.
                    </div>

                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#020073] text-white hover:bg-[#020073]/90"
                    >
                      Envoyer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Vue RECHERCHE BIENS */}
      {view === "recherche" && (
        <RechercheBiens onBack={() => setView("home")} />
      )}

      {/* Footer */}
      <footer className="border-t bg-[#020073] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 text-sm grid md:grid-cols-2 gap-6">
          <div>
            <div className="font-semibold text-[#bf9959]">Ragot Conception</div>
            <div className="opacity-90 text-[#bf9959]">
              Maîtrise d&apos;œuvre • Neuf &amp; Rénovation
            </div>
            <div className="mt-2 opacity-90 text-[#bf9959]">
              SIREN 982 188 344 • APE 7112B
            </div>
          </div>
          <div className="md:text-right">
            <a
              href="/mentions-legales"
              className="hover:underline text-[#bf9959]"
            >
              Mentions légales
            </a>
            <span className="mx-2">•</span>
            <a
              href="/politique-de-confidentialite"
              className="hover:underline text-[#bf9959]"
            >
              Politique de confidentialité
            </a>
            <div className="mt-2 opacity-90 text-[#bf9959]">
              © {new Date().getFullYear()} Ragot Conception • Tous droits
              réservés.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}